import { useRouter } from 'next/router';
import React, { FunctionComponent, useEffect, useMemo } from 'react';
import debounce from 'lodash/debounce';
import { ComponentProps } from './redux';
import { notifyUser } from '@helpers/Pnotify';
import useLocale from '@modules/translate/useLocale';
import { INotifMessage } from '@modules/common/types';
import { mergeMap, Observable } from 'rxjs';
import { loadServerTranslates } from '@commands/translate';

const NotificationView: FunctionComponent<ComponentProps> = (props) => {
  const { error, success, loadUser } = props;
  const router = useRouter();
  const [, currentLanguage, translates] = useLocale();
  const errorMethod = useMemo(() => debounce((msg) => {
    const { message, title, duration, onClick } = msg;
    notifyUser(title, message, duration, onClick);
  }, 250), [translates]);

  const successMethod = useMemo(() => debounce(async (msg) => {
    const { message, title, duration, payload } = msg;
    let extraTitle = '';
    if (payload && payload.userId) {
      const item = await loadUser(payload.userId);
      if (item) {
        extraTitle = ` ${item.user.firstName} ${item.user?.surname}`;
      }
    }

    notifyUser(title + extraTitle, message, duration, payload?.link ? () => {
      router.push(payload.link);
    } : () => {
    });
  }, 250), [translates]);

  useEffect(() => {

    const subscriptionError = error
      .pipe(mergeMap((msg: INotifMessage) => {
        return new Observable<INotifMessage>((observer) => {
          const messageKey = msg.title || 'errors.title';
          const descriptionKey = msg.message;

          loadServerTranslates(currentLanguage, [messageKey, descriptionKey], false).then(({ list }) => {
            msg.title = list[messageKey as string] || messageKey;
            msg.message = list[descriptionKey as string] || descriptionKey;
            observer.next(msg);
            observer.complete();
          });
        });
      }))
      .subscribe(errorMethod);

    const subscriptionSuccess = success.pipe(mergeMap((msg: INotifMessage) => {
      return new Observable<INotifMessage>((observer) => {
        const messageKey = msg.title || 'common.success';
        const descriptionKey = msg.message;

        loadServerTranslates(currentLanguage, [messageKey, descriptionKey], false).then(({ list }) => {
          msg.title = list[messageKey as string] || messageKey;
          msg.message = list[descriptionKey as string] || descriptionKey;
          observer.next(msg);
          observer.complete();
        });
      });
    })).subscribe(successMethod);

    return () => {
      subscriptionError.unsubscribe();
      subscriptionSuccess.unsubscribe();
    };
  }, [errorMethod, successMethod, translates]);

  return null;
};

export default NotificationView;
